import { Notice, type Tone } from '@rental/ui'
import { type ReactNode, useCallback, useEffect, useState } from 'react'

/** Long enough to be read out; then gone, so nobody comes upon it later, out of its moment. */
const SAID_MS = 7_000

/**
 * How long the words of a status that comes with its screen wait (UI-2g): a
 * region inserted with its words is not read by every screen reader, while one
 * already there whose words change is; and by then the screen's title, which
 * takes the focus, has been read.
 */
const ARRIVAL_MS = 250

/** Words said once: a new id makes the same words a new saying. */
export interface Said {
  id: number
  text: string
}

/**
 * A sentence for a screen reader, said in a status that is on the page from
 * its start (UI-2g): the day an arrow leads to, the connection come back. Keyed,
 * so the same words said twice are heard twice; gone after a few seconds, as
 * Today's are.
 */
export function useSaid(): [Said | null, (text: string) => void] {
  const [said, setSaid] = useState<Said | null>(null)
  useEffect(() => {
    if (!said) return
    const timer = window.setTimeout(() => setSaid(null), SAID_MS)
    return () => window.clearTimeout(timer)
  }, [said])
  const say = useCallback(
    (text: string) => setSaid((last) => ({ id: (last?.id ?? 0) + 1, text })),
    [],
  )
  return [said, say]
}

/** The words `useSaid` holds, for the status that says them. */
export function SaidWords({ said }: { said: Said | null }) {
  return said ? <span key={said.id}>{said.text}</span> : null
}

/**
 * What a screen was opened to say: a booking created, a car handed over, a
 * notice registered (UI-2g). Its status comes with the screen and its words a
 * moment later, hidden until then: their room is kept, so nothing moves when
 * they show, and they are read after the title, which takes the focus.
 */
export function ArrivalNotice({
  tone = 'success',
  children,
}: {
  tone?: Tone
  children: ReactNode
}) {
  const [arrived, setArrived] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setArrived(true), ARRIVAL_MS)
    return () => window.clearTimeout(timer)
  }, [])
  return (
    <div role="status">
      <div className={arrived ? undefined : 'invisible'}>
        <Notice tone={tone}>{children}</Notice>
      </div>
    </div>
  )
}
